// src/components/imoveis/EncerrarAnuncioModal.jsx
import React, { useEffect, useState } from 'react';
import { IMaskInput } from 'react-imask';

/**
 * Modal para encerrar anúncio de imóvel.
 *
 * payload = {
 *   resultado: 'vendido' | 'perdido' | 'retirado' | 'erro',
 *   venda: resultado === 'vendido'
 *     ? {
 *         data: 'YYYY-MM-DD',
 *         clienteNome: string,
 *         produtoDescricao: string,
 *         valor: string,     // ex: "500000.00" (sem máscara)
 *         comissao: string,  // ex: "15000.00" (pode ser '' se não informado)
 *       }
 *     : null
 * }
 */

const capitalizeWords = (text = '') =>
  String(text)
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');

const EncerrarAnuncioModal = ({
  open,
  onClose,
  imovel,
  onConfirm,
  loading = false,
}) => {
  const [resultado, setResultado] = useState('');
  const [dataVenda, setDataVenda] = useState('');
  const [clienteNome, setClienteNome] = useState('');
  const [produtoDescricao, setProdutoDescricao] = useState('');
  const [valorVenda, setValorVenda] = useState('');
  const [comissao, setComissao] = useState('');
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!imovel) return;

    const partes = [];
    if (imovel.tipo) partes.push(imovel.tipo);
    if (imovel.bairro) partes.push(imovel.bairro);
    if (imovel.cidade) partes.push(imovel.cidade);
    const desc =
      partes.length > 0
        ? partes.join(' - ')
        : `Imóvel ${imovel.refCodigo || imovel.referencia || ''}`.trim();

    setProdutoDescricao(desc);

    // data padrão: hoje
    const hoje = new Date();
    const yyyy = hoje.getFullYear();
    const mm = String(hoje.getMonth() + 1).padStart(2, '0');
    const dd = String(hoje.getDate()).padStart(2, '0');
    setDataVenda(`${yyyy}-${mm}-${dd}`);

    // valor padrão: preço do imóvel (se existir)
    if (imovel.preco || imovel.valor) {
      const bruto = imovel.preco ?? imovel.valor;
      const digits = String(bruto).replace(/[^\d]/g, '');
      if (digits) setValorVenda(digits); // cai como centavos se vc quiser, mas aqui estamos usando Number mask (scale 2)
    }
  }, [imovel]);

  useEffect(() => {
    if (!open) {
      setResultado('');
      setDataVenda('');
      setClienteNome('');
      setProdutoDescricao('');
      setValorVenda('');
      setComissao('');
      setErro('');
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;

    if (!resultado) {
      setErro('Selecione o motivo para encerrar o anúncio.');
      return;
    }

    if (resultado === 'vendido') {
      if (!dataVenda || !clienteNome.trim() || !produtoDescricao.trim()) {
        setErro('Preencha data, cliente e descrição do produto.');
        return;
      }
      if (!valorVenda) {
        setErro('Informe o valor da venda.');
        return;
      }
    }

    setErro('');

    const payload = {
      resultado,
      venda:
        resultado === 'vendido'
          ? {
              data: dataVenda,
              // ✅ capitaliza
              clienteNome: capitalizeWords(clienteNome),
              // ✅ capitaliza (produto/descrição)
              produtoDescricao: capitalizeWords(produtoDescricao),
              // ✅ já vem sem máscara (unmask true)
              valor: String(valorVenda),
              comissao: comissao ? String(comissao) : '',
            }
          : null,
    };

    onConfirm?.(payload);
  };

  const isVenda = resultado === 'vendido';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
        {/* Cabeçalho */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900">
              Encerrar anúncio
            </h2>
            {imovel && (
              <p className="text-xs text-zinc-500 mt-0.5">
                Imóvel:{' '}
                <span className="font-medium">
                  {imovel.tipo || 'Imóvel'}
                  {imovel.bairro ? ` - ${imovel.bairro}` : ''}
                  {imovel.cidade ? ` (${imovel.cidade})` : ''}
                </span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => !loading && onClose?.()}
            disabled={loading}
            className="text-sm text-zinc-500 hover:text-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Fechar
          </button>
        </div>

        {erro && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {erro}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 text-sm">
          {/* Motivo / Resultado */}
          <div>
            <p className="mb-2 text-xs font-medium text-zinc-700">
              Qual o desfecho deste anúncio?
            </p>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <label className="flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50">
                <input
                  type="radio"
                  name="resultado"
                  value="vendido"
                  checked={resultado === 'vendido'}
                  onChange={(e) => setResultado(e.target.value)}
                  disabled={loading}
                />
                <span>Venda fechada</span>
              </label>

              <label className="flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50">
                <input
                  type="radio"
                  name="resultado"
                  value="perdido"
                  checked={resultado === 'perdido'}
                  onChange={(e) => setResultado(e.target.value)}
                  disabled={loading}
                />
                <span>Venda perdida (cliente comprou outro / desistiu)</span>
              </label>

              <label className="flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50">
                <input
                  type="radio"
                  name="resultado"
                  value="retirado"
                  checked={resultado === 'retirado'}
                  onChange={(e) => setResultado(e.target.value)}
                  disabled={loading}
                />
                <span>Parar de atender / retirar do ar</span>
              </label>

              <label className="flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50">
                <input
                  type="radio"
                  name="resultado"
                  value="erro"
                  checked={resultado === 'erro'}
                  onChange={(e) => setResultado(e.target.value)}
                  disabled={loading}
                />
                <span>Cadastro duplicado / erro</span>
              </label>
            </div>
          </div>

          {/* Se for venda, exibir os campos extras */}
          {isVenda && (
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4 space-y-3">
              <p className="text-xs font-medium text-emerald-800 mb-1">
                Dados da venda
              </p>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="text-[11px] font-medium text-zinc-600">
                    Data da venda
                  </label>
                  <input
                    type="date"
                    value={dataVenda}
                    onChange={(e) => setDataVenda(e.target.value)}
                    disabled={loading}
                    className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-medium text-zinc-600">
                    Nome do cliente
                  </label>
                  <input
                    type="text"
                    value={clienteNome}
                    onChange={(e) => setClienteNome(e.target.value)}
                    onBlur={() => setClienteNome(capitalizeWords(clienteNome))}
                    placeholder="Ex.: João Silva"
                    disabled={loading}
                    className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] font-medium text-zinc-600">
                  Produto / descrição
                </label>
                <input
                  type="text"
                  value={produtoDescricao}
                  onChange={(e) => setProdutoDescricao(e.target.value)}
                  onBlur={() =>
                    setProdutoDescricao(capitalizeWords(produtoDescricao))
                  }
                  disabled={loading}
                  className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
                />
                <p className="mt-1 text-[11px] text-zinc-500">
                  Você pode ajustar a descrição se quiser algo mais específico.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="text-[11px] font-medium text-zinc-600">
                    Valor da venda (R$)
                  </label>
                  <IMaskInput
                    mask="R$ num"
                    blocks={{
                      num: {
                        mask: Number,
                        thousandsSeparator: '.',
                        radix: ',',
                        scale: 2,
                        mapToRadix: [','],
                        // ✅ sempre com centavos (unmask vira ex: "7410000.00")
                        padFractionalZeros: true,
                        normalizeZeros: true,
                        signed: false,
                      },
                    }}
                    unmask={true}
                    value={valorVenda}
                    onAccept={(v) => setValorVenda(v)}
                    placeholder="Ex.: 650000,00"
                    disabled={loading}
                    className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-medium text-zinc-600">
                    Comissão (R$) – opcional
                  </label>
                  <IMaskInput
                    mask="R$ num"
                    blocks={{
                      num: {
                        mask: Number,
                        thousandsSeparator: '.',
                        radix: ',',
                        scale: 2,
                        mapToRadix: [','],
                        // ✅ sempre com centavos
                        padFractionalZeros: true,
                        normalizeZeros: true,
                        signed: false,
                      },
                    }}
                    unmask={true}
                    value={comissao}
                    onAccept={(v) => setComissao(v)}
                    placeholder="Ex.: 19500,00"
                    disabled={loading}
                    className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Botões */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => !loading && onClose?.()}
              disabled={loading}
              className="rounded-xl border px-3 py-2 text-xs font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Encerrando...' : 'Confirmar encerramento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EncerrarAnuncioModal;
